import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import clsx from 'clsx';
import { InspectorHeader } from '../InspectorHeader/InspectorHeader';
import IconPlayCircle from '@/assets/icons/icon-play-circle-02.svg?react';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import { Event } from '@/components/dropdowns/Event/Event';
import { ScrollArea } from '@/components/ui/scroll-area';
import NodeOpacitySettings from '@/components/inspector-fields/SliderField/NodeOpacitySettings';
import NodeStrokeSettings from '@/components/inspector-fields/SliderField/NodeStrokeSettings';
import NodeColorField from '@/components/inspector-fields/ColorField/NodeColorField';
import { useNode } from '@craftjs/core';
import type { VideoProps } from '@builder4/shared/components/Video';
import { LayoutElementV2 } from '@/components/inspector-fields/LayoutElement/LayoutElementV2';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import NodeAnimationSettings from '@/components/inspector-fields/AnimationSettings/NodeAnimationSettings';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { parseVideoUrl, validateEmbedCode } from '@builder4/shared/utils/videoHelpers';
import NodeVideoPreviewSettings from '@/components/inspector-fields/VideoPreview/NodeVideoPreviewSettings';
import type { VideoSourceType, VideoFitType, VideoProvider } from '@builder4/shared/types/video';
import NodeShadowField from '@/components/inspector-fields/ShadowField/NodeShadowField';
import { useAppDispatch } from '@/store/hooks';
import { showToast } from '@/store/notifications/slice';

interface InspectorVideoProps {
  className?: string;
  text?: string;
}

export const InspectorVideo: React.FC<InspectorVideoProps> = ({ className, text = 'Video' }) => {
  const dispatch = useAppDispatch();
  const {
    actions: { setProp },
    props,
    displayName,
  } = useNode((node) => ({
    props: node.data.props as VideoProps,
    displayName: node.data.displayName,
  }));

  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);

  // Get current prop values (declared early for state initialization)
  const sourceType = getBPProp(props, 'sourceType') || 'url';
  const videoProvider = getBPProp(props, 'videoProvider') || 'youtube';
  const url = getBPProp(props, 'url') || '';
  const embedCode = getBPProp(props, 'embedCode') || '';

  // Local state for URL and embed inputs (allows typing without updating props on every keystroke)
  const [urlInput, setUrlInput] = useState(url);
  const [embedInput, setEmbedInput] = useState(embedCode);
  const [embedError, setEmbedError] = useState<string | null>(null);

  // Sync URL input with breakpoint-aware prop (for external changes like undo/redo)
  useEffect(() => {
    setUrlInput(url);
  }, [url]);

  // Sync embed input with breakpoint-aware prop (for external changes like undo/redo)
  useEffect(() => {
    setEmbedInput(embedCode);
  }, [embedCode]);

  // Toggle state for source type selector
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false);
  const sourceTypeRefs = useRef<Record<VideoSourceType, HTMLButtonElement | null>>({
    url: null,
    embed: null,
  });

  // Update toggle highlight position when source type changes
  useLayoutEffect(() => {
    const activeEl = sourceTypeRefs.current[sourceType];
    if (activeEl) {
      setHighlightStyle({
        width: activeEl.offsetWidth,
        left: activeEl.offsetLeft,
      });
    }
  }, [sourceType]);

  // Get remaining prop values
  const fitType = getBPProp(props, 'fitType') || 'fill';
  const autoplay = getBPProp(props, 'autoplay');
  const loop = getBPProp(props, 'loop');
  const mute = getBPProp(props, 'mute');
  const controls = getBPProp(props, 'controls');

  // WHY: Browsers require mute for autoplay to work, so mute is always forced on when autoplay is enabled
  const isMuteForced = Boolean(autoplay);
  const effectiveMute = isMuteForced || Boolean(mute);

  // WHY: Loom, Vidyard, and Brightcove don't support loop via embed URL params
  const PROVIDERS_WITHOUT_LOOP: VideoProvider[] = ['loom', 'vidyard', 'brightcove'];
  const isLoopUnsupported = PROVIDERS_WITHOUT_LOOP.includes(videoProvider as VideoProvider);

  // Handle URL submission (on blur or Enter)
  const handleUrlSubmit = () => {
    const trimmedUrl = urlInput.trim();

    if (!trimmedUrl) {
      setProp((props: VideoProps) => {
        setBPProp(props, 'url', '');
      }, 500);
      return;
    }

    const parsed = parseVideoUrl(trimmedUrl);

    if (!parsed.isValid) {
      dispatch(
        showToast({
          text: 'Invalid video URL. Please check and try again.',
          state: 'error',
          icon: 'error',
        }),
      );
      return;
    }

    setProp((props: VideoProps) => {
      setBPProp(props, 'url', trimmedUrl);
      setBPProp(props, 'videoProvider', parsed.provider);
    }, 500);
  };

  // Handle source type change (toggle)
  const handleSourceTypeChange = (newType: VideoSourceType) => {
    setIsAnimated(true);
    setProp((props: VideoProps) => {
      setBPProp(props, 'sourceType', newType);
    }, 500);
    setEmbedError(null);
  };

  // Handle video provider change
  const handleProviderChange = (value: string) => {
    setProp((props: VideoProps) => {
      setBPProp(props, 'videoProvider', value as VideoProvider);
    }, 500);
  };

  // Handle embed code submit (validates on blur or Enter)
  const handleEmbedCodeSubmit = useCallback(() => {
    const trimmedCode = embedInput.trim();

    if (!trimmedCode) {
      setEmbedError(null);
      setProp((props: VideoProps) => {
        setBPProp(props, 'embedCode', '');
      }, 500);
      return;
    }

    const validation = validateEmbedCode(trimmedCode);
    if (!validation.isValid) {
      setEmbedError(validation.error || 'Invalid embed code');
      dispatch(
        showToast({
          text: validation.error || 'Invalid embed code. Please check and try again.',
          state: 'error',
          icon: 'error',
        }),
      );
      return;
    }

    setEmbedError(null);

    setProp((props: VideoProps) => {
      setBPProp(props, 'embedCode', trimmedCode);
    }, 500);
  }, [embedInput, setProp, setBPProp, dispatch]);

  // Handle autoplay change
  const handleAutoplayChange = (value: string) => {
    setProp((props: VideoProps) => {
      setBPProp(props, 'autoplay', value === 'yes');
    }, 500);
  };

  // Handle loop change (no-op for providers that don't support loop)
  const handleLoopChange = (value: string) => {
    if (isLoopUnsupported) {
      return;
    }
    setProp((props: VideoProps) => {
      setBPProp(props, 'loop', value === 'yes');
    }, 500);
  };

  // Handle mute change
  const handleMuteChange = (value: string) => {
    setProp((props: VideoProps) => {
      setBPProp(props, 'mute', value === 'yes');
    }, 500);
  };

  // Handle controls change
  const handleControlsChange = (value: string) => {
    setProp((props: VideoProps) => {
      setBPProp(props, 'controls', value === 'yes');
    }, 500);
  };

  // Handle fit type change
  const handleFitTypeChange = (value: string) => {
    setProp((props: VideoProps) => {
      setBPProp(props, 'fitType', value as VideoFitType);
    }, 500);
  };

  return (
    <div
      className={clsx(
        'inspector bg-neutral-0 relative z-10 inline-flex h-[calc(100vh-52px)] max-w-[240px] flex-col border-l border-neutral-100',
        className,
      )}
      data-testid="inspector-video"
    >
      <InspectorHeader
        className="max-w-[240px] text-neutral-900"
        icon={IconPlayCircle}
        text={text}
      />
      <ScrollArea className="h-[calc(100vh-104px)] overflow-hidden">
        <div className="flex flex-col gap-3 p-3">
          <LayoutElementV2
            className=""
            state="default"
            data-qa-selector="inspector-video-layout-element"
          />
          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Video</p>

          {/* Source Type Toggle */}
          <div className="flex w-full max-w-[216px] items-center justify-between gap-4">
            <p className="text-button-sm font-medium text-neutral-500">Type</p>
            <div className="relative flex h-8 w-[160px] items-center rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
              <div
                className={clsx(
                  'absolute top-0 z-20 flex h-full items-center justify-center rounded-md bg-[#ffffff] text-indigo-500 outline outline-offset-[-1px] outline-[#e9ecf0]',
                  isAnimated && 'transition-all duration-300',
                )}
                style={{
                  width: `${highlightStyle.width}px`,
                  transform: `translateX(${highlightStyle.left}px)`,
                }}
              >
                <p className="text-button-sm font-semibold capitalize">
                  {sourceType === 'url' ? 'URL' : 'Embed'}
                </p>
              </div>
              {(['url', 'embed'] as const).map((type) => (
                <button
                  key={type}
                  ref={(el) => {
                    sourceTypeRefs.current[type] = el;
                  }}
                  onClick={() => handleSourceTypeChange(type)}
                  className="text-button-sm relative z-10 flex-1 cursor-pointer px-2 py-1.5 text-neutral-400 capitalize"
                  data-testid={`source-type-${type}`}
                  data-qa-selector={`inspector-video-source-type-${type}`}
                >
                  {type === 'url' ? 'URL' : 'Embed'}
                </button>
              ))}
            </div>
          </div>

          {/* URL Input */}
          {sourceType === 'url' && (
            <Input
              className="max-w-[216px]"
              placeholder="Paste video URL"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onBlur={handleUrlSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleUrlSubmit();
              }}
              data-qa-selector="inspector-video-url-input"
            />
          )}

          {/* Provider/Type Selection (only for URL type) */}
          {sourceType === 'url' && (
            <SelectField
              onValueChange={handleProviderChange}
              className="max-w-[216px]"
              defaultValue={videoProvider}
              size="medium"
              text="Type"
              data-qa-selector="inspector-video-provider"
            >
              <SelectItem value="youtube">YouTube</SelectItem>
              <SelectItem value="vimeo">Vimeo</SelectItem>
              <SelectItem value="loom">Loom</SelectItem>
              <SelectItem value="wistia">Wistia</SelectItem>
              <SelectItem value="vidyard">Vidyard</SelectItem>
              <SelectItem value="brightcove">Brightcove</SelectItem>
              <SelectItem value="cloudflare-stream">Cloudflare Stream</SelectItem>
            </SelectField>
          )}

          {/* Embed Code Input */}
          {sourceType === 'embed' && (
            <>
              <Textarea
                className={clsx('max-w-[216px] resize-none', embedError && 'border-red-500')}
                placeholder="Paste iframe embed code"
                value={embedInput}
                onChange={(e) => setEmbedInput(e.target.value)}
                onBlur={handleEmbedCodeSubmit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleEmbedCodeSubmit();
                  }
                }}
                rows={3}
                data-qa-selector="inspector-video-embed-code"
              />
              {embedError && <span className="text-xs text-red-500">{embedError}</span>}
            </>
          )}

          {/* Video Preview */}
          <NodeVideoPreviewSettings mode={sourceType} data-qa-selector="inspector-video-preview" />

          {/* Mode Selection */}
          <SelectField
            onValueChange={handleFitTypeChange}
            className="max-w-[216px]"
            defaultValue={fitType}
            size="small"
            text="Mode"
            data-qa-selector="inspector-video-mode"
          >
            <SelectItem value="fill">Fill</SelectItem>
            <SelectItem value="fit">Fit</SelectItem>
          </SelectField>

          <NodeOpacitySettings data-qa-selector="inspector-video-opacity" />

          {/* Autoplay Setting */}
          <SelectField
            onValueChange={handleAutoplayChange}
            className="max-w-[216px]"
            defaultValue={autoplay ? 'yes' : 'no'}
            size="small"
            text="Autoplay"
            data-qa-selector="inspector-video-autoplay"
          >
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectField>

          {/* Loop Setting */}
          <SelectField
            onValueChange={handleLoopChange}
            className="max-w-[216px]"
            defaultValue={isLoopUnsupported ? 'no' : loop ? 'yes' : 'no'}
            size="small"
            text="Loop"
            showInfoIcon={isLoopUnsupported}
            infoTooltipText="Not supported by this provider"
            data-qa-selector="inspector-video-loop"
          >
            {isLoopUnsupported ? (
              <SelectItem value="no">No</SelectItem>
            ) : (
              <>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </>
            )}
          </SelectField>

          {/* Mute Setting */}
          <SelectField
            onValueChange={isMuteForced ? undefined : handleMuteChange}
            className="max-w-[216px]"
            defaultValue={effectiveMute ? 'yes' : 'no'}
            size="small"
            text="Mute"
            showInfoIcon={isMuteForced}
            infoTooltipText="Required for autoplay"
            data-qa-selector="inspector-video-mute"
          >
            {isMuteForced ? (
              <SelectItem value="yes">Yes</SelectItem>
            ) : (
              <>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </>
            )}
          </SelectField>

          {/* Controls Setting */}
          <SelectField
            onValueChange={handleControlsChange}
            className="max-w-[216px]"
            defaultValue={controls ? 'yes' : 'no'}
            size="small"
            text="Controls"
            data-qa-selector="inspector-video-controls"
          >
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectField>

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Appearance</p>

          <NodeColorField
            propertyName="backgroundColor"
            solidPropertyName="bgSolid"
            gradientPropertyName="bgGradient"
            gradientEnabled
            label="Fill"
            data-qa-selector="inspector-video-fill"
          />

          <NodeColorField
            propertyName="borderColor"
            ensureStrokeVisibleProp="borderWidth"
            label="Stroke Color"
            data-qa-selector="inspector-video-stroke-color"
          />

          <NodeStrokeSettings data-qa-selector="inspector-video-stroke" />

          <NodeShadowField data-qa-selector="inspector-video-shadow" />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Interaction</p>
          <Event
            className=""
            selectValue="nolink"
            text="Event"
            data-qa-selector="inspector-video-event"
          />
          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
          <NodeAnimationSettings />
        </div>
      </ScrollArea>
    </div>
  );
};
